import React, {useState, useEffect} from 'react';
import {Image, TouchableOpacity, View, ActivityIndicator, SafeAreaView} from 'react-native';
import {Images, logoRefri} from '../../constants/Images';
import {Colors} from '../../constants/Colors';
import {Text} from 'react-native';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import MenuModal from '../MenuModal/MenuModal';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import {useUserStore} from '../../store/UserStore';
import {useBagStore} from '../../store/BagStore';
import {useModalStore} from '../../store/MenuModalStore';
import {useNotificationStore} from '../../store/NotificationStore';
import {useFavoritesStore} from '../../store/FavoritesStore';
import { Dimensions } from 'react-native';
import NotificationService from '../../services/NotificationsService/NotificationsService';
import HomeInput from '../HomeInput/HomeInput';
import { useIsDesktop } from '../../hooks/useIsDesktop';

interface IHeader {
  justDropDown?: boolean;
  width?: string;
  refreshing?: boolean;
  setSearchOnParams?: (search: string) => void;
}

const Header = ({justDropDown = true, width, setSearchOnParams, refreshing }: IHeader) => {
  const isDesktop = useIsDesktop();
  const {navigate} = useNavigation<NavigationProps>();
  const {user} = useUserStore();
  const [showModal, setShowModal] = useState<boolean>(false);
  const totalItems = useBagStore(state => state.getTotalItems());
  const notificationCount = useNotificationStore(state => state.notificationCount);
  const setNotificationCount = useNotificationStore(state => state.setNotificationCount);
  const [loadingNotifications, setLoadingNotifications] = useState<boolean>(false);
  const isModalOpen = useModalStore(state => state.isModalOpen);
  const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  const favoriteCount = useFavoritesStore((state) => state.favoriteCount);

  const storeAccess = !!user?.storeAccess;
  const screenWidth = Dimensions.get('window').width;

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const response = await NotificationService.GetCountNotifications();
      setNotificationCount(response.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    if (refreshing) {
      fetchNotifications();
    }
  }, [refreshing]);

  useFocusEffect(
    React.useCallback(() => {
      fetchNotifications();
    }, [])
  );

  // Submete a busca: abre a tela de catálogo com o termo pesquisado
  const submitSearch = () => {
    const term = search.trim();
    if (!term) return;
    setShowSearchInput(false);
    navigate(
      AppRoutes.CATALOG_BROWSE as never,
      {filter: {searchQuery: term, title: 'Busca'}} as never,
    );
  };

  return (
    <View>
      {/* Header principal reorganizado */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: width || '100%',
          paddingHorizontal: isDesktop ? 8 : 0,
          paddingTop: 8,
          paddingBottom: 8,
          backgroundColor: '#fff',
          ...(isDesktop ? { borderBottomWidth: 1, borderBottomColor: '#f0f0f0', marginBottom: 8 } : {}),
        }}
      >
        {/* Área esquerda: botões favoritos e notificações */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: isDesktop ? 'auto' : 64,
            height: 48,
            zIndex: 2,
            ...(isDesktop ? { gap: 8 } : {}),
          }}
        >
          {/* Botão de Notificações */}
          <TouchableOpacity
            onPress={() => {
              setShowModal(false);
              navigate(AppRoutes.NOTIFICATIONS);
            }}
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              width: 32,
              height: 48,
              marginRight: 10,
            }}
            activeOpacity={0.7}
          >
            <Image source={Images.bell} style={{width: 24, height: 24}} />
            {loadingNotifications ? (
              <ActivityIndicator
                size="small"
                color="red"
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                }}
              />
            ) : (
              notificationCount > 0 && (
                <View
                  style={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    backgroundColor: 'red',
                    borderRadius: 32,
                    minWidth: 18,
                    height: 18,
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingHorizontal: 2,
                    paddingVertical: 2,
                  }}>
                  <Text
                    style={{
                      color: 'white',
                      fontSize: notificationCount >= 100 ? 8 : 10,
                      fontFamily: 'TCCC-UnityHeadline Regular',
                      lineHeight: 14,
                      fontWeight: 'bold',
                      textAlign: 'center',
                      includeFontPadding: false,
                    }}>
                    {notificationCount >= 100 ? '99+' : notificationCount}
                  </Text>
                </View>
              )
            )}
          </TouchableOpacity>
          {/* Botão de Favoritos */}
          <TouchableOpacity
              onPress={() => navigate(AppRoutes.FAVORITE_PRODUCTS)}
          >
             <FontAwesome name="heart-o" size={24} color={Colors.GrayPallete.grayOne} />
            {favoriteCount > 0 && (
              <View
                style={{
                  position: 'absolute',
                  top: -15,
                  right: -13,
                  backgroundColor: 'red',
                  borderRadius: 32,
                  minWidth: 20,
                  height: 20,
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingHorizontal: 2,
                  paddingVertical: 2,
                }}>
                <Text
                  style={{
                    color: 'white',
                    fontSize: favoriteCount >= 100 ? 8 : 10,
                    fontFamily: 'TCCC-UnityHeadline Regular',
                    lineHeight: 14,
                    fontWeight: 'bold',
                    textAlign: 'center',
                    includeFontPadding: false,
                  }}>
                  {favoriteCount >= 100 ? '99+' : favoriteCount}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
        {/* Área central: logo */}
        <View style={{
          flex: 1,
          alignItems: 'center',
          justifyContent: 'center',
          height: 48,
        }}>
          <Image
            source={Images.logoRefri}
            style={{height: 38, resizeMode: 'contain'}}
          />
        </View>
        {/* Área direita: busca */}
        {isDesktop && setSearchOnParams ? (
          <View
            style={{
              flex: 1,
              maxWidth: 400,
              height: 48,
              justifyContent: 'center',
            }}
          >
            <HomeInput
              value={search}
              onPress={submitSearch}
              placeholder="O que você procura?"
              onChangeText={setSearch}
              onSubmitEditing={submitSearch}
              returnKeyType="search"
              showOkButton
            />
          </View>
        ) : (
          <View
            style={{
              width: 64,
              height: 48,
              alignItems: 'flex-end',
              justifyContent: 'center',
              flexDirection: 'row',
            }}
          >
            {setSearchOnParams && (
              <TouchableOpacity
                onPress={() => setShowSearchInput(!showSearchInput)}
                style={{
                  width: 48,
                  height: 48,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Image
                  source={Images.glass}
                  style={{
                    width: 22,
                    height: 22,
                    tintColor: Colors.GrayPallete.grayOne,
                  }}
                />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {/* Input de busca simplificado (mobile only) */}
      {!isDesktop && showSearchInput && setSearchOnParams && (
        <View
          style={{
            width: '100%',
            backgroundColor: '#fff',
            paddingHorizontal: 16,
            paddingVertical: 10,
            shadowColor: '#000',
            shadowOffset: {width: 0, height: 2},
            shadowOpacity: 0.1,
            shadowRadius: 4,
            elevation: 8,
          }}
        >
          <HomeInput
            value={search}
            onPress={submitSearch}
            placeholder="O que você procura?"
            onChangeText={setSearch}
            onSubmitEditing={submitSearch}
            returnKeyType="search"
            showOkButton
          />
        </View>
      )}

      {/* Modal de menu (com alteração) */}
      <MenuModal
        onPressEmpresas={() => {
          setShowModal(false);
          // Navigate to main tab and open company modal in Home
          (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, { screen: 'Home', params: { openCompanyModal: true } });
        }}
        onPress={() => setShowModal(true)}
        onRequestClose={() => setShowModal(false)}
        visible={showModal || isModalOpen}
      />
    </View>
  );
};

export default Header;
